import { router, Link } from '@inertiajs/react'
import { useState, useEffect, useRef } from 'react'
import AppLayout from '../../components/AppLayout'
import { useTheme } from '../../contexts/ThemeContext'

const RARITY_LEVELS = [
  { key: '-2', label: '定番',   bg: '#3a5a3a', text: '#fff' },
  { key: '-1', label: '準定番', bg: '#5b7d4f', text: '#fff' },
  { key: '0',  label: '標準',   bg: '#c8ccc4', text: '#444' },
  { key: '1',  label: '準珍',   bg: '#c9963f', text: '#fff' },
  { key: '2',  label: '珍しい', bg: '#c0463a', text: '#fff' },
]

const VEGTYPE_META = {
  T: { label: 'キホン',   bg: '#e0e0e0', text: '#555' },
  S: { label: '目玉',     bg: '#e07b39', text: '#fff' },
  C: { label: '変わり種', bg: '#7058a3', text: '#fff' },
  A: { label: 'おまけ',   bg: '#aaa',    text: '#fff' },
  O: { label: 'その他',   bg: '#ccc',    text: '#555' },
}

const SEASON_META = {
  '推し旬': { bg: '#c9963f', text: '#fff', icon: '★' },
  '旬':     { bg: '#4a7c59', text: '#fff', icon: '◎' },
  '避':     { bg: '#c0463a', text: '#fff', icon: '✕' },
  '通常':   { bg: '#e4e4e4', text: '#aaa', icon: '' },
}
const SEASON_OPTIONS = [ '推し旬', '旬', '通常', '避', null ]

const MONTHS = [1,2,3,4,5,6,7,8,9,10,11,12]

const COLOR_HASH = {
  赤: '#e60033', 黒: '#2b2b2b', 紫: '#65318e', 白: '#f8fbf8', 橙: '#f08300',
  緑: '#3eb370', 薄: '#69b076', 深: '#00552e', 黄: '#ffec47', 茶: '#783c1d',
  桃: '#f09199', 桜: '#fef4f4', 茜: '#b7282e', 肌: '#fce2c4', 若: '#98d98e',
  浅: '#88cb7f', 青: '#00a497', 紺: '#223a70', 菫: '#7058a3', 紅: '#d7003a',
  苗: '#b0ca71', 柳: '#a8c97f', 錆: '#6c3524', 栗: '#762f07', 褐: '#8a3b00',
  金: '#e6b422',
}
function colorHex(name) { return name ? (COLOR_HASH[name] || null) : null }

// CSS zoom が <html> に適用されている場合、getBoundingClientRect の値を
// レイアウト座標系（position:fixed が使う座標）に変換する
function fixedPos(rect) {
  const zoom = parseFloat(document.documentElement.style.zoom) || 1
  return { x: rect.left / zoom, y: rect.bottom / zoom }
}

function getCsrf() {
  return document.querySelector('meta[name="csrf-token"]')?.content || ''
}

function RarityBadge({ score }) {
  const r = RARITY_LEVELS.find(r => r.key === String(score))
  if (!r) return <span style={{ fontSize: '10px', color: '#aaa' }}>—</span>
  return <span style={{ fontSize: '10px', padding: '1px 7px', borderRadius: '3px', background: r.bg, color: r.text, fontWeight: 500, whiteSpace: 'nowrap' }}>{r.label}</span>
}

function VegtypeBadge({ flag }) {
  const m = VEGTYPE_META[flag]
  if (!m) return <span style={{ fontSize: '10px', color: '#aaa' }}>—</span>
  return <span style={{ fontSize: '10px', padding: '1px 7px', borderRadius: '3px', background: m.bg, color: m.text, fontWeight: 500, whiteSpace: 'nowrap' }}>{m.label}</span>
}

// ── 品目タグチップ（追加・削除） ─────────────────────────────────────────
function TagsCell({ itemId, tags, onRemove, onAdd }) {
  const [inputVisible, setInputVisible] = useState(false)
  const [newTag, setNewTag] = useState('')
  const inputRef = useRef(null)

  useEffect(() => { if (inputVisible) inputRef.current?.focus() }, [inputVisible])

  function handleAddSubmit() {
    const t = newTag.trim()
    if (t) onAdd(itemId, t)
    setNewTag('')
    setInputVisible(false)
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', alignItems: 'center', marginTop: (tags.length > 0 || inputVisible) ? '3px' : '0' }}>
      {tags.map(tag => (
        <span key={tag} onClick={() => onRemove(itemId, tag)} title="クリックで削除"
          style={{ fontSize: '9px', padding: '1px 6px', borderRadius: '10px', background: '#e0e7ff', color: '#3730a3', cursor: 'pointer', border: '1px solid #c7d2fe', whiteSpace: 'nowrap' }}>
          {tag}
        </span>
      ))}
      {inputVisible ? (
        <input ref={inputRef} value={newTag} onChange={e => setNewTag(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleAddSubmit(); if (e.key === 'Escape') { setInputVisible(false); setNewTag('') } }}
          onBlur={handleAddSubmit}
          placeholder="タグ名…"
          style={{ fontSize: '9px', width: '60px', padding: '1px 4px', border: '1px solid #c7d2fe', borderRadius: '3px', outline: 'none' }} />
      ) : (
        <span onClick={() => setInputVisible(true)} title="タグを追加"
          style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '10px', background: 'transparent', color: '#94a3b8', cursor: 'pointer', border: '1px dashed #cbd5e1' }}>
          ＋
        </span>
      )}
    </div>
  )
}

// ── 旬カレンダーグリッド（当月ハイライトなし） ─────────────────────────
function SeasonGrid({ itemId, itemIndex, seasonByMonth, overrides, onCellClick, onDirectSelect, C }) {
  const merged = { ...seasonByMonth }
  if (overrides) {
    Object.entries(overrides).forEach(([m, s]) => {
      if (s) merged[parseInt(m)] = s
      else delete merged[parseInt(m)]
    })
  }

  function handleKeyDown(e, month) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onCellClick(e, itemId, month)
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      if (month > 1) document.querySelector(`[data-season-id="${itemId}"][data-season-month="${month - 1}"]`)?.focus()
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      if (month < 12) document.querySelector(`[data-season-id="${itemId}"][data-season-month="${month + 1}"]`)?.focus()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      document.querySelector(`[data-season-index="${itemIndex - 1}"][data-season-month="${month}"]`)?.focus()
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      document.querySelector(`[data-season-index="${itemIndex + 1}"][data-season-month="${month}"]`)?.focus()
    } else if (['1', '2', '3', '4'].includes(e.key)) {
      onDirectSelect(itemId, month, ['推し旬', '旬', '通常', '避'][parseInt(e.key) - 1])
    } else if (e.key === '0' || e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault()
      onDirectSelect(itemId, month, null)
    }
  }

  return (
    <div style={{ display: 'flex', gap: '0' }}>
      {MONTHS.map(m => {
        const status = merged[m]
        const meta   = status ? SEASON_META[status] : null
        const groupGap = (m === 4 || m === 7 || m === 10) ? '4px' : '2px'
        return (
          <div key={m} style={{ marginLeft: m === 1 ? 0 : groupGap, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ fontSize: '8px', lineHeight: 1, marginBottom: '2px', color: C.inkMuted, fontWeight: 400, fontFamily: "'JetBrains Mono', monospace" }}>{m}</div>
            <div
              data-season-id={itemId}
              data-season-index={itemIndex}
              data-season-month={m}
              tabIndex={0}
              onClick={e => onCellClick(e, itemId, m)}
              onKeyDown={e => handleKeyDown(e, m)}
              onFocus={e => { e.currentTarget.style.boxShadow = '0 0 0 2px #4a7c59'; e.currentTarget.style.zIndex = '1' }}
              onBlur={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.zIndex = '' }}
              title={status ? `${m}月: ${status}` : `${m}月: 未登録`}
              style={{
                width: '16px', height: '20px', borderRadius: '2px',
                background: meta ? meta.bg : C.lineSoft,
                border: `1px solid ${C.lineSoft}`,
                display: 'grid', placeItems: 'center',
                fontSize: '8px', color: meta ? meta.text : 'transparent',
                fontWeight: 700, cursor: 'pointer',
                transition: 'transform 0.1s', outline: 'none', position: 'relative',
              }}
              onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.15)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
            >
              {meta?.icon || ''}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── 旬編集ポップオーバー ─────────────────────────────────────────────────
function SeasonPopover({ cell, onSelect, onClose, C }) {
  const ref = useRef(null)
  const btnRefs = useRef([])

  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) onClose() }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [onClose])

  useEffect(() => { btnRefs.current[0]?.focus() }, [])

  function handleKeyDown(e, index) {
    if (e.key === 'Escape') { e.preventDefault(); onClose() }
    else if (e.key === 'ArrowDown') { e.preventDefault(); btnRefs.current[Math.min(index + 1, SEASON_OPTIONS.length - 1)]?.focus() }
    else if (e.key === 'ArrowUp')   { e.preventDefault(); btnRefs.current[Math.max(index - 1, 0)]?.focus() }
    else if (['1','2','3','4','5'].includes(e.key)) {
      const idx = parseInt(e.key) - 1
      if (idx < SEASON_OPTIONS.length) onSelect(SEASON_OPTIONS[idx])
    }
  }

  return (
    <div ref={ref} style={{
      position: 'fixed', top: cell.y + 4, left: cell.x,
      background: C.paper, border: `1px solid ${C.line}`,
      borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
      overflow: 'hidden', zIndex: 9999, minWidth: '130px',
    }}>
      <div style={{ padding: '6px 10px', fontSize: '10px', color: C.inkMuted, borderBottom: `1px solid ${C.lineSoft}` }}>
        {cell.month}月のステータス
      </div>
      {SEASON_OPTIONS.map((s, i) => {
        const meta = s ? SEASON_META[s] : null
        return (
          <button key={s ?? 'none'} ref={el => { btnRefs.current[i] = el }}
            onClick={() => onSelect(s)} onKeyDown={e => handleKeyDown(e, i)}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '7px 12px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '12px', color: C.ink, textAlign: 'left', outline: 'none' }}
            onFocus={e => e.currentTarget.style.background = C.paper2}
            onBlur={e => e.currentTarget.style.background = 'transparent'}
            onMouseEnter={e => e.currentTarget.style.background = C.paper2}
            onMouseLeave={e => e.currentTarget.style.background = e.currentTarget === document.activeElement ? C.paper2 : 'transparent'}
          >
            <span style={{ fontSize: '9px', color: C.inkMuted, width: '10px', flexShrink: 0, textAlign: 'center' }}>{i + 1}</span>
            {meta
              ? <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: meta.bg, display: 'inline-block', flexShrink: 0 }} />
              : <span style={{ width: '10px', height: '10px', borderRadius: '2px', border: `1px solid ${C.line}`, display: 'inline-block', flexShrink: 0 }} />
            }
            {s ?? '削除（未登録）'}
          </button>
        )
      })}
    </div>
  )
}

// ── 珍しさ編集ポップオーバー ─────────────────────────────────────────────
const RARITY_OPTIONS = [...RARITY_LEVELS, null]

function RarityPopover({ cell, onSelect, onClose, C }) {
  const ref = useRef(null)
  const btnRefs = useRef([])

  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) onClose() }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [onClose])

  useEffect(() => { btnRefs.current[0]?.focus() }, [])

  function handleKeyDown(e, index) {
    if (e.key === 'Escape') { e.preventDefault(); onClose() }
    else if (e.key === 'ArrowDown') { e.preventDefault(); btnRefs.current[Math.min(index + 1, RARITY_OPTIONS.length - 1)]?.focus() }
    else if (e.key === 'ArrowUp')   { e.preventDefault(); btnRefs.current[Math.max(index - 1, 0)]?.focus() }
  }

  return (
    <div ref={ref} style={{
      position: 'fixed', top: cell.y + 4, left: cell.x,
      background: C.paper, border: `1px solid ${C.line}`,
      borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
      overflow: 'hidden', zIndex: 9999, minWidth: '120px',
    }}>
      <div style={{ padding: '6px 10px', fontSize: '10px', color: C.inkMuted, borderBottom: `1px solid ${C.lineSoft}` }}>
        珍しさを変更
      </div>
      {RARITY_OPTIONS.map((r, i) => (
        <button key={r ? r.key : 'none'} ref={el => { btnRefs.current[i] = el }}
          onClick={() => onSelect(r ? r.key : null)} onKeyDown={e => handleKeyDown(e, i)}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '7px 12px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '12px', color: C.ink, textAlign: 'left', outline: 'none' }}
          onFocus={e => e.currentTarget.style.background = C.paper2}
          onBlur={e => e.currentTarget.style.background = 'transparent'}
          onMouseEnter={e => e.currentTarget.style.background = C.paper2}
          onMouseLeave={e => e.currentTarget.style.background = e.currentTarget === document.activeElement ? C.paper2 : 'transparent'}
        >
          {r
            ? <span style={{ fontSize: '10px', padding: '1px 7px', borderRadius: '3px', background: r.bg, color: r.text, fontWeight: 500 }}>{r.label}</span>
            : <span style={{ fontSize: '10px', color: C.inkMuted }}>— 未設定</span>
          }
        </button>
      ))}
    </div>
  )
}

export default function ProductItems({ items, total, page, per_page, filters, categories, current_month }) {
  const { theme: C } = useTheme()
  const [q, setQ]                       = useState(filters.q || '')
  const [category, setCategory]         = useState(filters.category || '')
  const [rarity, setRarity]             = useState(filters.rarity || '')
  const [vegtype, setVegtype]           = useState(filters.vegtype || '')
  const [seasonStatus, setSeasonStatus] = useState(filters.season_status || '')
  const [hasReview, setHasReview]       = useState(filters.has_review || '')

  const [seasonOverrides, setSeasonOverrides] = useState({})
  const [editCell, setEditCell]               = useState(null)
  const [savingCell, setSavingCell]           = useState(null)

  const [rarityOverrides, setRarityOverrides] = useState({})
  const [editRarity, setEditRarity]           = useState(null)
  const [savingRarity, setSavingRarity]       = useState(null)

  const [tagOverrides, setTagOverrides] = useState({})

  const totalPages = Math.ceil(total / per_page)

  function buildQuery(overrides = {}) {
    const params = { q, category, rarity, vegtype, season_status: seasonStatus, has_review: hasReview, page: 1, ...overrides }
    return Object.entries(params).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&')
  }
  function applyFilters(overrides = {}) {
    const qs = buildQuery(overrides)
    router.visit(`/master/product_items${qs ? `?${qs}` : ''}`, { preserveScroll: false })
  }
  function goPage(p) {
    const qs = buildQuery({ page: p })
    router.visit(`/master/product_items?${qs}`, { preserveScroll: true })
  }

  function handleCellClick(e, itemId, month) {
    e.stopPropagation()
    const pos = fixedPos(e.currentTarget.getBoundingClientRect())
    setEditCell({ itemId, month, x: pos.x, y: pos.y, triggerEl: e.currentTarget })
  }

  function handleRarityClick(e, itemId) {
    e.stopPropagation()
    const pos = fixedPos(e.currentTarget.getBoundingClientRect())
    setEditRarity({ itemId, x: pos.x, y: pos.y, triggerEl: e.currentTarget })
  }

  async function postSeasonality(itemId, month, status) {
    setSavingCell(`${itemId}_${month}`)
    const year = new Date().getFullYear()
    try {
      const res = await fetch(`/master/product_items/${itemId}/seasonality`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrf() },
        body: JSON.stringify({ month, year, status: status || '' }),
      })
      if (res.ok) {
        setSeasonOverrides(prev => ({ ...prev, [itemId]: { ...(prev[itemId] || {}), [month]: status || null } }))
      }
    } finally { setSavingCell(null) }
  }

  async function postRarity(itemId, score) {
    setSavingRarity(itemId)
    try {
      const res = await fetch(`/master/product_items/${itemId}/rarity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrf() },
        body: JSON.stringify({ rarity_score: score }),
      })
      if (res.ok) setRarityOverrides(prev => ({ ...prev, [itemId]: score }))
    } finally { setSavingRarity(null) }
  }

  async function postTags(itemId, tags) {
    const res = await fetch(`/master/product_items/${itemId}/tags`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrf() },
      body: JSON.stringify({ item_tags: tags }),
    })
    if (res.ok) setTagOverrides(prev => ({ ...prev, [itemId]: tags }))
  }

  async function handleStatusSelect(status) {
    const { itemId, month, triggerEl } = editCell
    setEditCell(null)
    setTimeout(() => triggerEl?.focus(), 0)
    await postSeasonality(itemId, month, status)
  }

  async function handleDirectSelect(itemId, month, status) {
    await postSeasonality(itemId, month, status)
  }

  async function handleRaritySelect(score) {
    const { itemId, triggerEl } = editRarity
    setEditRarity(null)
    setTimeout(() => triggerEl?.focus(), 0)
    await postRarity(itemId, score)
  }

  function getItemTags(pi) {
    return pi.id in tagOverrides ? tagOverrides[pi.id] : (pi.item_tags || [])
  }

  function handleTagRemove(itemId, tag) {
    const pi = items.find(p => p.id === itemId)
    const current = getItemTags(pi)
    postTags(itemId, current.filter(t => t !== tag))
  }

  function handleTagAdd(itemId, tag) {
    const pi = items.find(p => p.id === itemId)
    const current = getItemTags(pi)
    if (!current.includes(tag)) postTags(itemId, [...current, tag])
  }

  function getRarityScore(pi) {
    return pi.id in rarityOverrides ? rarityOverrides[pi.id] : pi.rarity_score
  }

  const selectStyle = {
    fontFamily: 'inherit', fontSize: '12px', padding: '5px 10px',
    background: C.paper2, border: `1px solid ${C.line}`,
    borderRadius: '4px', color: C.ink, cursor: 'pointer',
  }

  return (
    <AppLayout bgColor={C.bg} maxWidth="1600px">
      {/* ── ツールバー ── */}
      <div style={{ marginBottom: '16px', paddingBottom: '14px', borderBottom: `1px solid ${C.line}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '8px' }}>
          <span style={{ fontFamily: "'Noto Serif JP', serif", fontSize: '18px', fontWeight: 500, color: C.ink }}>品目マスタ</span>
          <span style={{ fontSize: '12px', color: C.inkMuted }}>{total.toLocaleString()} 件</span>
          <div style={{ flex: 1 }} />
          <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && applyFilters()}
            placeholder="品目名で検索…" style={{ ...selectStyle, minWidth: '160px' }} />
          <select value={category} onChange={e => { setCategory(e.target.value); applyFilters({ category: e.target.value }) }} style={selectStyle}>
            <option value="">全カテゴリ</option>
            {(categories || []).map(c => <option key={c.code} value={c.code}>{c.name || c.code}</option>)}
          </select>
          <select value={rarity} onChange={e => { setRarity(e.target.value); applyFilters({ rarity: e.target.value }) }} style={selectStyle}>
            <option value="">全珍しさ</option>
            {RARITY_LEVELS.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
          <select value={vegtype} onChange={e => { setVegtype(e.target.value); applyFilters({ vegtype: e.target.value }) }} style={selectStyle}>
            <option value="">全タイプ</option>
            {Object.entries(VEGTYPE_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
          </select>
          <button onClick={() => applyFilters()} style={{ ...selectStyle, background: C.greenDark, color: C.paper, border: 'none', fontWeight: 500 }}>検索</button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '11px', color: C.inkMuted }}>絞り込み：</span>
          <select value={seasonStatus} onChange={e => { setSeasonStatus(e.target.value); applyFilters({ season_status: e.target.value }) }} style={selectStyle}>
            <option value="">今月の旬（全て）</option>
            {['推し旬', '旬', '避', '通常'].map(s => <option key={s} value={s}>{s}</option>)}
            <option value="none">未登録</option>
          </select>
          <select value={hasReview} onChange={e => { setHasReview(e.target.value); applyFilters({ has_review: e.target.value }) }} style={selectStyle}>
            <option value="">満足度（全て）</option>
            <option value="1">満足度データあり</option>
            <option value="0">満足度データなし</option>
          </select>
          {(seasonStatus || hasReview || rarity || vegtype || category || q) && (
            <button onClick={() => { setQ(''); setCategory(''); setRarity(''); setVegtype(''); setSeasonStatus(''); setHasReview(''); applyFilters({ q: '', category: '', rarity: '', vegtype: '', season_status: '', has_review: '' }) }}
              style={{ ...selectStyle, color: C.tomato, border: `1px solid ${C.tomato}`, background: 'transparent' }}>
              リセット
            </button>
          )}
        </div>
      </div>

      {/* ── テーブル ── */}
      <div style={{ background: C.paper, borderRadius: '10px', border: `1px solid ${C.line}`, boxShadow: '0 4px 14px rgba(60,50,30,0.07)', overflow: 'hidden', marginBottom: '16px' }}>
        {/* 凡例 */}
        <div style={{ padding: '7px 16px', background: C.paper2, borderBottom: `1px solid ${C.line}`, fontSize: '10px', color: C.inkMuted }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', marginBottom: '5px' }}>
            <span>旬カレンダー：</span>
            {Object.entries(SEASON_META).map(([s, m]) => (
              <span key={s} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: m.bg, display: 'inline-block' }} />
                {s}
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <span style={{ color: C.inkMuted, marginRight: '4px' }}>キーボード操作：</span>
            {[['Tab','セルにフォーカス'],['Enter / Space','ポップオーバーで編集'],['1','推し旬'],['2','旬'],['3','通常'],['4','避'],['0 / Del','削除'],['← →','同行・隣月へ'],['↑ ↓','上下の行へ']].map(([key, desc]) => (
              <span key={key} style={{ display: 'flex', alignItems: 'center', gap: '3px', marginRight: '4px' }}>
                <kbd style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '9px', padding: '1px 5px', background: C.paper, border: `1px solid ${C.line}`, borderRadius: '3px', boxShadow: '0 1px 0 rgba(0,0,0,0.12)', color: C.ink, whiteSpace: 'nowrap' }}>{key}</kbd>
                <span>{desc}</span>
              </span>
            ))}
          </div>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '90px' }} />
            <col style={{ width: '170px' }} />
            <col style={{ width: '68px' }} />
            <col style={{ width: '50px' }} />
            <col style={{ width: '36px' }} />
            <col style={{ width: '44px' }} />
            <col style={{ width: '80px' }} />
            <col style={{ width: '260px' }} />
            <col style={{ width: '72px' }} />
            <col style={{ width: '80px' }} />
          </colgroup>
          <thead>
            <tr style={{ background: C.paper2, borderBottom: `1px solid ${C.line}` }}>
              {['カテゴリ', '品目名', 'タイプ', '頻度', '色', 'ボリューム', '珍しさ', '旬カレンダー（1〜12月）', '満足度', 'ベストワン'].map((h, i) => (
                <th key={i} style={{ padding: '8px 10px', textAlign: 'left', fontSize: '10px', letterSpacing: '0.08em', color: C.inkMuted, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((pi, i) => {
              const currentRarity = getRarityScore(pi)
              const currentTags   = getItemTags(pi)
              return (
                <tr key={pi.id} style={{ borderBottom: i < items.length - 1 ? `1px solid ${C.lineSoft}` : 'none' }}>
                  <td style={{ padding: '8px 10px' }}>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11px', color: C.inkMuted }}>{pi.middle_category_code || '—'}</span>
                  </td>
                  <td style={{ padding: '8px 10px', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                      <div style={{ fontSize: '13px', color: C.ink, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pi.name}</div>
                      <Link href={`/item_analysis/${pi.item_code}`} style={{ fontSize: '9px', color: C.greenDark, textDecoration: 'none', flexShrink: 0, border: `1px solid ${C.greenDark}`, borderRadius: '3px', padding: '0px 4px', lineHeight: '14px' }}>分析</Link>
                      <Link href={`/master/product_items/${pi.id}/seasonality_history`} style={{ fontSize: '9px', color: '#64748b', textDecoration: 'none', flexShrink: 0, border: '1px solid #94a3b8', borderRadius: '3px', padding: '0px 4px', lineHeight: '14px' }}>履歴</Link>
                    </div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', color: C.inkMuted }}>{pi.item_code}</div>
                    <TagsCell itemId={pi.id} tags={currentTags} onRemove={handleTagRemove} onAdd={handleTagAdd} />
                  </td>
                  <td style={{ padding: '8px 10px' }}><VegtypeBadge flag={pi.vegtype_flag} /></td>
                  <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '12px', color: pi.planning_frequency ? C.ink : C.inkMuted }}>
                      {pi.planning_frequency ? `1/${pi.planning_frequency}` : '—'}
                    </span>
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    <div style={{ display: 'flex', gap: '3px' }}>
                      {pi.color1 && <span title={pi.color1} style={{ width: '12px', height: '12px', borderRadius: '50%', background: colorHex(pi.color1) || C.lineSoft, border: '1px solid rgba(0,0,0,0.1)', display: 'inline-block' }} />}
                      {pi.color2 && <span title={pi.color2} style={{ width: '12px', height: '12px', borderRadius: '50%', background: colorHex(pi.color2) || C.lineSoft, border: '1px solid rgba(0,0,0,0.1)', display: 'inline-block' }} />}
                      {!pi.color1 && !pi.color2 && <span style={{ fontSize: '10px', color: C.inkMuted }}>—</span>}
                    </div>
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                    <span style={{ fontSize: '11px', color: C.inkSoft }}>{pi.volume || '—'}</span>
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    <div onClick={e => handleRarityClick(e, pi.id)} title="クリックして変更"
                      style={{ cursor: 'pointer', opacity: savingRarity === pi.id ? 0.4 : 1, transition: 'opacity 0.2s', display: 'inline-block' }}>
                      <RarityBadge score={currentRarity} />
                    </div>
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    <div style={{ opacity: savingCell && savingCell.startsWith(`${pi.id}_`) ? 0.4 : 1, transition: 'opacity 0.2s' }}>
                      <SeasonGrid
                        itemId={pi.id} itemIndex={i}
                        seasonByMonth={pi.season_by_month} overrides={seasonOverrides[pi.id]}
                        onCellClick={handleCellClick} onDirectSelect={handleDirectSelect} C={C}
                      />
                    </div>
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                    {pi.satisfaction_score != null ? (
                      <div>
                        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '12px', color: C.greenDark, fontWeight: 600 }}>{(pi.satisfaction_score * 100).toFixed(0)}%</div>
                        <div style={{ fontSize: '9px', color: C.inkMuted }}>{pi.satisfaction_count}件</div>
                      </div>
                    ) : <span style={{ fontSize: '10px', color: C.inkMuted }}>—</span>}
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                    {pi.best_one_ratio != null ? (
                      <div>
                        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '12px', color: C.gold, fontWeight: 600 }}>★{(pi.best_one_ratio * 100).toFixed(1)}%</div>
                        <div style={{ fontSize: '9px', color: C.inkMuted }}>{pi.best_one_wins}/{pi.best_one_total}</div>
                      </div>
                    ) : <span style={{ fontSize: '10px', color: C.inkMuted }}>—</span>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* ── ページネーション ── */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
          <button onClick={() => goPage(page - 1)} disabled={page <= 1}
            style={{ padding: '5px 12px', borderRadius: '4px', border: `1px solid ${C.line}`, background: C.paper2, color: page <= 1 ? C.inkMuted : C.ink, cursor: page <= 1 ? 'default' : 'pointer', fontSize: '12px' }}>←</button>
          {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
            const p = totalPages <= 10 ? i + 1 : page <= 5 ? i + 1 : page >= totalPages - 4 ? totalPages - 9 + i : page - 4 + i
            return (
              <button key={p} onClick={() => goPage(p)}
                style={{ padding: '5px 10px', borderRadius: '4px', border: `1px solid ${p === page ? C.greenDark : C.line}`, background: p === page ? C.greenDark : C.paper2, color: p === page ? C.paper : C.ink, cursor: 'pointer', fontSize: '12px', fontWeight: p === page ? 700 : 400 }}>
                {p}
              </button>
            )
          })}
          <button onClick={() => goPage(page + 1)} disabled={page >= totalPages}
            style={{ padding: '5px 12px', borderRadius: '4px', border: `1px solid ${C.line}`, background: C.paper2, color: page >= totalPages ? C.inkMuted : C.ink, cursor: page >= totalPages ? 'default' : 'pointer', fontSize: '12px' }}>→</button>
          <span style={{ fontSize: '11px', color: C.inkMuted, marginLeft: '8px' }}>{page} / {totalPages} ページ</span>
        </div>
      )}

      {editCell && (
        <SeasonPopover cell={editCell} onSelect={handleStatusSelect} onClose={() => {
          const el = editCell?.triggerEl; setEditCell(null); setTimeout(() => el?.focus(), 0)
        }} C={C} />
      )}

      {editRarity && (
        <RarityPopover cell={editRarity} onSelect={handleRaritySelect} onClose={() => {
          const el = editRarity?.triggerEl; setEditRarity(null); setTimeout(() => el?.focus(), 0)
        }} C={C} />
      )}
    </AppLayout>
  )
}
